import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

function InlineText({ children }) {
  const pattern = /(\*\*[^*]+\*\*|https?:\/\/[^\s)]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g;
  const parts = children.split(pattern).filter(Boolean);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const inner = part.slice(2, -2);
      if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(inner)) {
        return <strong key={index}><a href={`mailto:${inner}`}>{inner}</a></strong>;
      }
      if (/^https?:\/\//.test(inner)) {
        return <strong key={index}><a href={inner}>{inner}</a></strong>;
      }
      return <strong key={index}>{inner}</strong>;
    }
    if (/^https?:\/\//.test(part)) {
      const trailing = part.match(/[.,;:]$/)?.[0] || "";
      const href = trailing ? part.slice(0, -1) : part;
      return <React.Fragment key={index}><a href={href}>{href}</a>{trailing}</React.Fragment>;
    }
    if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(part)) {
      return <a key={index} href={`mailto:${part}`}>{part}</a>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function MarkdownDocument({ content }) {
  const lines = content.trim().split("\n");
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();
    if (!line) { index += 1; continue; }
    if (line === "---") { blocks.push(<hr key={index} />); index += 1; continue; }

    if (line.startsWith("## ")) {
      blocks.push(<h2 key={index}><InlineText>{line.slice(3)}</InlineText></h2>);
      index += 1; continue;
    }
    if (line.startsWith("### ")) {
      blocks.push(<h3 key={index}><InlineText>{line.slice(4)}</InlineText></h3>);
      index += 1; continue;
    }
    if (line.startsWith("- ")) {
      const items = [];
      const key = index;
      while (index < lines.length && lines[index].trim().startsWith("- ")) {
        items.push(<li key={index}><InlineText>{lines[index].trim().slice(2)}</InlineText></li>);
        index += 1;
      }
      blocks.push(<ul key={key}>{items}</ul>); continue;
    }
    if (line.startsWith("|")) {
      const rows = [];
      const key = index;
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        const cells = lines[index].trim().slice(1, -1).split("|").map((cell) => cell.trim());
        if (!cells.every((cell) => /^-+$/.test(cell))) rows.push(cells);
        index += 1;
      }
      blocks.push(
        <div className="legal-table-wrap" key={key}>
          <table><thead><tr>{rows[0].map((cell, i) => <th key={i}><InlineText>{cell}</InlineText></th>)}</tr></thead>
          <tbody>{rows.slice(1).map((row, r) => <tr key={r}>{row.map((cell, c) => <td key={c}><InlineText>{cell}</InlineText></td>)}</tr>)}</tbody></table>
        </div>
      ); continue;
    }

    const paragraph = [];
    const key = index;
    while (index < lines.length) {
      const current = lines[index].trim();
      if (!current || current === "---" || current.startsWith("## ") || current.startsWith("### ") || current.startsWith("- ") || current.startsWith("|")) break;
      paragraph.push(current);
      index += 1;
    }
    const text = paragraph.join(" ");
    const italic = text.startsWith("*") && text.endsWith("*") && !text.startsWith("**");
    blocks.push(italic ? <p className="legal-note" key={key}><em><InlineText>{text.slice(1, -1)}</InlineText></em></p> : <p key={key}><InlineText>{text}</InlineText></p>);
  }
  return blocks;
}

export default function LegalDocument({ title, description, content, alternate }) {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content");
    document.title = `${title} | Kingswake`;
    meta?.setAttribute("content", description);
    window.scrollTo(0, 0);
    return () => {
      document.title = previousTitle;
      if (previousDescription) meta?.setAttribute("content", previousDescription);
    };
  }, [title, description]);

  return (
    <div data-testid="jantaquid-legal-page">
      <Navbar />
      <main className="legal-page">
        <article className="container-kw legal-shell">
          <Link to="/" className="legal-back"><ArrowLeft aria-hidden="true" /> Back to Kingswake</Link>
          <div className="eyebrow legal-eyebrow">Kingswake · Jantaquid · Legal</div>
          <h1>{title}</h1>
          <p className="legal-updated">Last updated: <time dateTime="2026-07-31">31 July 2026</time></p>
          <div className="legal-document"><MarkdownDocument content={content} /></div>
          <aside className="legal-crosslink" aria-label="Related legal document">
            <span>Jantaquid legal</span>
            <Link to={alternate.href}>{alternate.label}<ArrowRight aria-hidden="true" /></Link>
          </aside>
        </article>
      </main>
      <Footer />
    </div>
  );
}
